import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { parseShelfOnlyBinCode, withShelfSlot } from '../utils/warehouseLocationParser';
import './ShelfSlotPicker.css';

export const SHELF_SLOT_COUNT = 10;

/** Number keys 1-9 pick that slot, 0 picks 10. Returns null for any other key. */
export function slotForKey(key, slotCount = SHELF_SLOT_COUNT) {
  if (!/^[0-9]$/.test(String(key))) return null;
  const slot = key === '0' ? 10 : Number(key);
  return slot <= slotCount ? slot : null;
}

/**
 * Force a slot onto a shelf-only bin code (Rhys 2026-10-01). Staff save codes
 * like T-05-A that name a shelf but not where on it; this shows the shelf as
 * ten positions left to right and the tap becomes the slot (T-05-A-04).
 *
 * Used by every location entry point that saves a code (Products card, Put
 * Away, stocktake Relocate and Lookup edit). Portalled to <body> above the
 * picking relocate layer and Polaris modals. Touch: 64px+ tall targets.
 * Keyboard: 1-9 and 0 (=10) pick, arrows move, Escape cancels.
 *
 * @param {{ shelfCode: string, onPick: (code: string) => void, onCancel: () => void, slotCount?: number }} props
 */
export default function ShelfSlotPicker({ shelfCode, onPick, onCancel, slotCount = SHELF_SLOT_COUNT }) {
  const shelf = parseShelfOnlyBinCode(shelfCode);
  const [preview, setPreview] = useState(null);
  const slotRefs = useRef([]);
  const dialogRef = useRef(null);
  const code = shelf?.code || String(shelfCode || '');

  // Latest handlers for the window listener below (registered once).
  const latest = useRef({});
  latest.current = { code, onPick, onCancel, slotCount };

  useEffect(() => {
    dialogRef.current?.focus();
    // Window capture phase so the keys never reach the sheets underneath
    // (their document-level Escape would close them, and the stocktake
    // keyboard wedge would steal digits into the tray box).
    const onKey = (event) => {
      const { code: shelfCode, onPick: pickCb, onCancel: cancelCb, slotCount: count } = latest.current;
      if (event.key === 'Escape') {
        // Cancel on keyup: Polaris Modal closes on Escape KEYUP, so the
        // picker must still be listening then to swallow it.
        event.preventDefault();
        event.stopPropagation();
        if (event.type === 'keyup') cancelCb();
        return;
      }
      if (event.type !== 'keydown') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const slot = slotForKey(event.key, count);
      if (slot) {
        event.preventDefault();
        event.stopPropagation();
        const next = withShelfSlot(shelfCode, slot);
        if (next) pickCb(next);
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        event.stopPropagation();
        const current = slotRefs.current.indexOf(document.activeElement);
        const step = event.key === 'ArrowRight' ? 1 : -1;
        const from = current < 0 ? (step > 0 ? -1 : count) : current;
        const next = Math.min(count - 1, Math.max(0, from + step));
        slotRefs.current[next]?.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
    };
  }, []);

  const pick = (slot) => {
    const next = withShelfSlot(code, slot);
    if (next) onPick(next);
  };

  const slots = Array.from({ length: slotCount }, (_, i) => i + 1);

  return createPortal(
    // data-portal-id: Polaris Modal's focus trap leaves portal content alone,
    // so the picker keeps focus when opened over Put Away.
    <div
      className="ShelfSlotPicker-backdrop"
      data-portal-id="shelf-slot-picker"
      onClick={(event) => { event.stopPropagation(); onCancel(); }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div
        ref={dialogRef}
        className="ShelfSlotPicker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shelf-slot-picker-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <p className="ShelfSlotPicker-title" id="shelf-slot-picker-title">
          Where on shelf <span className="ShelfSlotPicker-code">{code}</span>?
        </p>
        <p className="ShelfSlotPicker-hint">
          Tap roughly where the item sits. 1 is the left end. Keys 1–9, 0 for 10.
        </p>
        <div className="ShelfSlotPicker-shelf" role="group" aria-label={`Shelf ${code}, left to right`}>
          <div className="ShelfSlotPicker-slots" style={{ gridTemplateColumns: `repeat(${slotCount}, minmax(0, 1fr))` }}>
            {slots.map((slot, i) => (
              <button
                key={slot}
                ref={(el) => { slotRefs.current[i] = el; }}
                type="button"
                className="ShelfSlotPicker-slot"
                aria-label={`Slot ${slot}: ${withShelfSlot(code, slot) || ''}`}
                onClick={() => pick(slot)}
                onMouseEnter={() => setPreview(slot)}
                onFocus={() => setPreview(slot)}
                onMouseLeave={() => setPreview(null)}
              >
                {slot}
              </button>
            ))}
          </div>
          <div className="ShelfSlotPicker-lip" aria-hidden="true" />
        </div>
        <p className="ShelfSlotPicker-preview" aria-live="polite">
          {preview ? withShelfSlot(code, preview) : `${code}-??`}
        </p>
        <div className="ShelfSlotPicker-actions">
          <button type="button" className="ShelfSlotPicker-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
